import { useFirebaseApp } from 'vuefire'
import {
  type Analytics,
  type CustomParams,
  initializeAnalytics,
  isSupported,
  setUserId,
  setUserProperties,
} from 'firebase/analytics'
import { getAuth, getIdTokenResult, onAuthStateChanged } from 'firebase/auth'
import {
  type DataSnapshot,
  getDatabase,
  off,
  onValue,
  ref,
  type DatabaseReference,
} from '@firebase/database'
import { getFunctions } from '@firebase/functions'
import { REGION } from '~/constants/firebase'
import { Locations } from '~/constants/firebase/realtime-database'
import useUser from '~/composables/useUser'

interface AnalyticsUserProperties extends CustomParams {
  email: string | null
  displayName: string | null
}

const isProduction = process.env.NODE_ENV === 'production'

export default defineNuxtPlugin(async () => {
  let callback: ((snapshot: DataSnapshot) => Promise<void>) | null = null
  let metadataRef: DatabaseReference | null = null

  const functions = getFunctions()
  functions.region = REGION

  let analytics: Analytics | null = null
  const firebaseApp = useFirebaseApp()
  const executedIsSupported = await isSupported()
  if (isProduction) {
    if (executedIsSupported) {
      analytics = initializeAnalytics(firebaseApp)
    }
    else {
      console.warn('Analytics not supported.')
    }
  }

  const auth = getAuth()
  const canUseAnalytics = isProduction && analytics
  onAuthStateChanged(auth, async (user) => {
    const { $sentrySetUser } = useNuxtApp()
    const { setClaims } = useUser()

    // Remove previous listener.
    if (metadataRef && callback) {
      off(metadataRef, 'value', callback)
    }

    if (user) {
      if (canUseAnalytics) {
        setUserId(analytics!, user.uid)
        setUserProperties(analytics!, {
          email: user.email,
          displayName: user.displayName,
        } as AnalyticsUserProperties)
      }

      metadataRef = ref(
        getDatabase(),
        Locations.Metadata.RefreshTime(user.uid),
      )
      callback = async () => {
        await user.getIdToken(true) // Force refresh to pick up the latest custom claims changes.

        const idTokenResult = await getIdTokenResult(user)
        setClaims(idTokenResult.claims)

        $sentrySetUser({
          id: user.uid,
          email: user.email as string,
          displayName: user.displayName as string,
        })
      }

      // Subscribe new listener to changes on that node
      onValue(metadataRef, callback)
    }
    else {
      setClaims(null)
      $sentrySetUser(null)

      if (canUseAnalytics) {
        setUserId(analytics!, null)
        setUserProperties(analytics!, {
          email: null,
          displayName: null,
        } as AnalyticsUserProperties)
      }
    }
  })
})
